import { Helmet } from "react-helmet-async";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Mail, MessageSquare, Clock, MapPin } from "lucide-react";

const Contact = () => {
  return (
    <Layout>
      <Helmet>
        <title>Contact Us — Datalevo Tools | Walking Weight Loss Calculator</title>
        <meta
          name="description"
          content="Get in touch with the Datalevo team. We're here to help with questions about our Walking Weight Loss Calculator and other tools."
        />
        <link rel="canonical" href="https://walkingweightlosscalculator.datalevo.com/contact" />
      </Helmet>

      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Have questions, feedback, or suggestions? We'd love to hear from you. Our team typically responds within 24-48 hours.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          <Card className="p-6 text-center">
            <div className="inline-flex p-3 rounded-full bg-primary/10 mb-4">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-semibold text-foreground mb-2">Email Support</h2>
            <p className="text-sm text-muted-foreground mb-3">
              For general inquiries and support
            </p>
            <a
              href="mailto:contact@datalevo.com"
              className="text-primary hover:underline font-medium"
            >
              contact@datalevo.com
            </a>
          </Card>

          <Card className="p-6 text-center">
            <div className="inline-flex p-3 rounded-full bg-accent/10 mb-4">
              <MessageSquare className="h-6 w-6 text-accent" />
            </div>
            <h2 className="font-semibold text-foreground mb-2">Feedback</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Help us improve our tools
            </p>
            <p className="text-sm text-muted-foreground">
              Use the feedback button on any tool page
            </p>
          </Card>

          <Card className="p-6 text-center md:col-span-2 lg:col-span-1">
            <div className="inline-flex p-3 rounded-full bg-primary/10 mb-4">
              <Clock className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-semibold text-foreground mb-2">Response Time</h2>
            <p className="text-sm text-muted-foreground mb-3">
              We aim to respond within
            </p>
            <p className="font-semibold text-foreground">24-48 hours</p>
          </Card>
        </div>

        {/* FAQ-like section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Before You Reach Out
          </h2>
          
          <div className="space-y-4">
            <Card className="p-5">
              <h3 className="font-semibold text-foreground mb-2">
                Having issues with the calculator?
              </h3>
              <p className="text-sm text-muted-foreground">
                Try refreshing your browser or clearing your cache. Our calculator runs entirely in your browser, so most issues can be resolved this way. If the problem persists, please include your browser name and version in your email.
              </p>
            </Card>

            <Card className="p-5">
              <h3 className="font-semibold text-foreground mb-2">
                Want to suggest a feature?
              </h3>
              <p className="text-sm text-muted-foreground">
                We love hearing your ideas! Please use the feedback feature on the tool page or email us with your suggestion. Include details about how the feature would help your fitness journey.
              </p>
            </Card>

            <Card className="p-5">
              <h3 className="font-semibold text-foreground mb-2">
                Questions about methodology?
              </h3>
              <p className="text-sm text-muted-foreground">
                You can download our complete methodology PDF from the verification section on the calculator page. It includes all formulas, citations, and assumptions we use.
              </p>
            </Card>

            <Card className="p-5">
              <h3 className="font-semibold text-foreground mb-2">
                Business inquiries?
              </h3>
              <p className="text-sm text-muted-foreground">
                For partnership opportunities, white-label solutions, or embedding our tools on your website, please email us at contact@datalevo.com with "Business Inquiry" in the subject line.
              </p>
            </Card>
          </div>
        </div>

        {/* Location info (optional) */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>Serving users worldwide from our digital headquarters</span>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Contact;
